import type React from 'react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, onSnapshot, query, where, orderBy } from 'firebase/firestore';
import type { GameQuestion, GameSession } from '@/types/models';
import { db } from '@/lib/firebase/client';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { useAuth } from '@/features/auth/useAuth';
import { createSession, patchSession } from '@/features/game/api';
import { PRESET_PACKS } from '@/features/game/data/presetQuestions';
import { buildDefaultDesignPartnerConfig } from '@/features/game/data/designPartnerDefaults';
import { useReveal } from '@/lib/hooks/useReveal';

const statusLabel: Record<GameSession['status'], string> = {
  waiting: 'Waiting',
  active: 'Live',
  ended: 'Ended',
};
const statusColor: Record<GameSession['status'], string> = {
  waiting: 'bg-yellow-100 text-yellow-700',
  active: 'bg-green-100 text-green-700',
  ended: 'bg-[#f5f5f5] text-[#aaaaaa]',
};

type QuestionSource = 'word-cloud' | 'tweet' | 'design-partner' | string;

function groupSessionsByDay(sessions: GameSession[]): Array<[string, GameSession[]]> {
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startOfYesterday = startOfToday - 24 * 60 * 60 * 1000;
  const formatter = new Intl.DateTimeFormat(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

  const groups = new Map<string, { label: string; sessions: GameSession[] }>();
  for (const s of sessions) {
    const d = new Date(s.createdAt);
    const dayStart = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const key = String(dayStart);
    const label = dayStart === startOfToday ? 'Today' : dayStart === startOfYesterday ? 'Yesterday' : formatter.format(d);
    const existing = groups.get(key);
    if (existing) existing.sessions.push(s);
    else groups.set(key, { label, sessions: [s] });
  }
  return Array.from(groups.entries())
    .sort((a, b) => Number(b[0]) - Number(a[0]))
    .map(([, v]) => [v.label, v.sessions] as [string, GameSession[]]);
}

export function DashboardPage() {
  const { user } = useAuth();
  useReveal();
  const [sessions, setSessions] = useState<GameSession[]>([]);
  const [sessionTitle, setSessionTitle] = useState('');
  const [questionSource, setQuestionSource] = useState<QuestionSource>('word-cloud');
  const [wordCloudPrompts, setWordCloudPrompts] = useState<string[]>(['']);
  const [tweetPrompts, setTweetPrompts] = useState<string[]>(['']);
  const [creating, setCreating] = useState(false);
  const [newSession, setNewSession] = useState<GameSession | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    const q = query(
      collection(db, 'gameSessions'),
      where('hostUid', '==', user.uid),
      orderBy('createdAt', 'desc'),
    );
    return onSnapshot(q, (snap) => {
      setSessions(snap.docs.map((d) => d.data() as GameSession));
    });
  }, [user]);

  function buildQuestions(): GameQuestion[] {
    if (questionSource === 'design-partner') return [];
    if (questionSource === 'word-cloud') {
      return wordCloudPrompts
        .filter((p) => p.trim().length > 0)
        .map((prompt, i) => ({
          id: `wc-custom-${i}`,
          type: 'word-cloud' as const,
          prompt: prompt.trim(),
          maxChars: 150,
          points: 100,
        }));
    }
    if (questionSource === 'tweet') {
      return tweetPrompts
        .filter((p) => p.trim().length > 0)
        .map((prompt, i) => ({
          id: `tw-custom-${i}`,
          type: 'tweet' as const,
          prompt: prompt.trim(),
          maxChars: 150,
          points: 100,
        }));
    }
    const pack = PRESET_PACKS.find((p) => p.id === questionSource);
    return pack?.questions ?? [];
  }

  async function handleCreateSession() {
    const isDesignPartner = questionSource === 'design-partner';
    const questions = buildQuestions();
    if (!isDesignPartner && questions.length === 0) {
      setCreateError('No questions found for the selected source.');
      return;
    }

    const defaultTitle = isDesignPartner
      ? 'AI Design Partner'
      : (PRESET_PACKS.find((p) => p.id === questionSource)?.name ?? 'Game Session');

    setCreateError(null);
    setCreating(true);
    try {
      const s = await createSession({
        courseId: 'preset',
        title: sessionTitle || defaultTitle,
        questions: questions.slice(0, 30),
        ...(isDesignPartner
          ? { mode: 'design-partner' as const, designPartner: buildDefaultDesignPartnerConfig() }
          : {}),
      });
      setNewSession(s);
      setSessionTitle('');
      setQuestionSource('word-cloud');
      setWordCloudPrompts(['']);
      setTweetPrompts(['']);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create session.');
    } finally {
      setCreating(false);
    }
  }

  const [closingId, setClosingId] = useState<string | null>(null);

  async function handleCloseSession(sessionId: string) {
    setClosingId(sessionId);
    try {
      await patchSession(sessionId, { status: 'ended' });
    } finally {
      setClosingId(null);
    }
  }

  const selectedPack = PRESET_PACKS.find((p) => p.id === questionSource);
  const canCreate =
    questionSource === 'word-cloud' ? wordCloudPrompts.some((p) => p.trim().length > 0)
    : questionSource === 'tweet' ? tweetPrompts.some((p) => p.trim().length > 0)
    : true;

  return (
    <div className="max-w-3xl">
      <div className="mb-8 reveal">
        <h1 className="font-serif text-3xl font-bold text-[#1a1a1a] mb-1">Dashboard</h1>
        <p className="text-[#555555]">Welcome back, {user?.displayName ?? user?.email}.</p>
      </div>

      {/* Create Session */}
      <section className="mb-10 reveal" style={{ animationDelay: '60ms' }}>
        <h2 className="text-lg font-semibold text-[#1a1a1a] mb-4">Create a Game Session</h2>

        {newSession ? (
          <Card variant="primary" className="p-6 text-center">
            <p className="text-white/80 text-sm mb-2">Session created! Share this code:</p>
            <p className="font-mono text-6xl font-black tracking-widest text-white mb-1 game-pop">{newSession.code}</p>
            <p className="text-white/60 text-xs mb-5 font-mono">learn.seytravel.com/join</p>
            <div className="flex gap-3 justify-center">
              <Link to={`/dashboard/sessions/${newSession.id}`}>
                <Button
                  variant="raised"
                  style={{ '--btn-depth': 'rgba(0,0,0,0.2)' } as React.CSSProperties}
                >
                  Open Host View
                </Button>
              </Link>
              <Button
                variant="ghost"
                onClick={() => setNewSession(null)}
                className="text-white/80 hover:text-white hover:bg-white/10"
              >
                Create Another
              </Button>
            </div>
          </Card>
        ) : (
          <Card className="p-6">
            <div className="space-y-4">
              {/* Question source selector */}
              <div>
                <label className="block text-sm font-semibold text-[#1a1a1a] mb-3">Question source</label>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                  <button
                    onClick={() => setQuestionSource('word-cloud')}
                    className={`rounded-xl border-2 px-4 py-3 text-left text-sm transition-all duration-150 ${questionSource === 'word-cloud' ? 'border-brand-400 bg-brand-50 text-brand-700 shadow-sm' : 'border-[#eeeeee] text-[#555555] hover:border-brand-200 hover:bg-brand-50/50'}`}
                  >
                    <div className="text-2xl mb-1">☁️</div>
                    <div className="font-semibold">Word Cloud</div>
                    <div className="text-xs mt-0.5 opacity-70">Collect free-text responses, live</div>
                  </button>
                  <button
                    onClick={() => setQuestionSource('tweet')}
                    className={`rounded-xl border-2 px-4 py-3 text-left text-sm transition-all duration-150 ${questionSource === 'tweet' ? 'border-brand-400 bg-brand-50 text-brand-700 shadow-sm' : 'border-[#eeeeee] text-[#555555] hover:border-brand-200 hover:bg-brand-50/50'}`}
                  >
                    <div className="text-2xl mb-1">🐦</div>
                    <div className="font-semibold">Tweet</div>
                    <div className="text-xs mt-0.5 opacity-70">Mock Twitter — app + handle + tweet, live feed</div>
                  </button>
                  <button
                    onClick={() => setQuestionSource('design-partner')}
                    className={`rounded-xl border-2 px-4 py-3 text-left text-sm transition-all duration-150 ${questionSource === 'design-partner' ? 'border-brand-400 bg-brand-50 text-brand-700 shadow-sm' : 'border-[#eeeeee] text-[#555555] hover:border-brand-200 hover:bg-brand-50/50'}`}
                  >
                    <div className="text-2xl mb-1">💬</div>
                    <div className="font-semibold">AI Design Partner</div>
                    <div className="text-xs mt-0.5 opacity-70">4 MCQs, then a private chat with Flo</div>
                  </button>
                  {PRESET_PACKS.map((pack) => (
                    <button
                      key={pack.id}
                      onClick={() => setQuestionSource(pack.id)}
                      className={`rounded-xl border-2 px-4 py-3 text-left text-sm transition-all duration-150 ${questionSource === pack.id ? 'border-brand-400 bg-brand-50 text-brand-700 shadow-sm' : 'border-[#eeeeee] text-[#555555] hover:border-brand-200 hover:bg-brand-50/50'}`}
                    >
                      <div className="text-2xl mb-1">{pack.icon}</div>
                      <div className="font-semibold">{pack.name}</div>
                      <div className="text-xs mt-0.5 opacity-70">{pack.questions.length} questions</div>
                    </button>
                  ))}
                </div>
                {selectedPack && (
                  <p className="mt-2 text-xs text-[#aaaaaa]">{selectedPack.description}</p>
                )}
              </div>

              {/* Word Cloud prompt builder */}
              {questionSource === 'word-cloud' && (
                <div>
                  <label className="block text-sm font-semibold text-[#1a1a1a] mb-2">Questions</label>
                  <div className="space-y-2">
                    {wordCloudPrompts.map((prompt, i) => (
                      <div key={i} className="flex gap-2 items-start">
                        <input
                          type="text"
                          value={prompt}
                          onChange={(e) => {
                            const next = [...wordCloudPrompts];
                            next[i] = e.target.value;
                            setWordCloudPrompts(next);
                          }}
                          placeholder={`Question ${i + 1} — e.g. "What's your biggest money worry?"`}
                          className="flex-1 rounded-lg border border-[#eeeeee] px-3 py-2 text-sm text-[#1a1a1a] focus:border-brand-400 focus:outline-none"
                        />
                        {wordCloudPrompts.length > 1 && (
                          <button
                            type="button"
                            onClick={() => setWordCloudPrompts(wordCloudPrompts.filter((_, j) => j !== i))}
                            className="mt-0.5 text-[#cccccc] hover:text-red-400 transition-colors text-lg leading-none px-1"
                            aria-label="Remove question"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  {wordCloudPrompts.length < 10 && (
                    <button
                      type="button"
                      onClick={() => setWordCloudPrompts([...wordCloudPrompts, ''])}
                      className="mt-2 text-xs text-brand-600 hover:text-brand-800 font-medium"
                    >
                      + Add another question
                    </button>
                  )}
                  <p className="mt-2 text-xs text-[#aaaaaa]">
                    Each question becomes a round. Players type a free-text answer and everyone earns 100 pts for responding.
                  </p>
                </div>
              )}

              {/* Tweet prompt builder */}
              {questionSource === 'tweet' && (
                <div>
                  <label className="block text-sm font-semibold text-[#1a1a1a] mb-2">Tweet prompts</label>
                  <div className="space-y-2">
                    {tweetPrompts.map((prompt, i) => (
                      <div key={i} className="flex gap-2 items-start">
                        <input
                          type="text"
                          value={prompt}
                          onChange={(e) => {
                            const next = [...tweetPrompts];
                            next[i] = e.target.value;
                            setTweetPrompts(next);
                          }}
                          placeholder={`Prompt ${i + 1} — e.g. "Pitch your app in a single tweet."`}
                          className="flex-1 rounded-lg border border-[#eeeeee] px-3 py-2 text-sm text-[#1a1a1a] focus:border-brand-400 focus:outline-none"
                        />
                        {tweetPrompts.length > 1 && (
                          <button
                            type="button"
                            onClick={() => setTweetPrompts(tweetPrompts.filter((_, j) => j !== i))}
                            className="mt-0.5 text-[#cccccc] hover:text-red-400 transition-colors text-lg leading-none px-1"
                            aria-label="Remove prompt"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  {tweetPrompts.length < 10 && (
                    <button
                      type="button"
                      onClick={() => setTweetPrompts([...tweetPrompts, ''])}
                      className="mt-2 text-xs text-brand-600 hover:text-brand-800 font-medium"
                    >
                      + Add another prompt
                    </button>
                  )}
                  <p className="mt-2 text-xs text-[#aaaaaa]">
                    Each prompt becomes a round. Players post a mock tweet (app name + handle + 150 chars). Tweets stream onto the display in real time; players only see their own.
                  </p>
                </div>
              )}

              {/* Session title */}
              <div>
                <label className="block text-sm font-medium text-[#1a1a1a] mb-1">Session title (optional)</label>
                <input
                  type="text"
                  value={sessionTitle}
                  onChange={(e) => setSessionTitle(e.target.value)}
                  placeholder={selectedPack?.name ?? 'Game Session'}
                  className="w-full rounded-lg border border-[#eeeeee] px-3 py-2 text-sm text-[#1a1a1a] focus:border-brand-400 focus:outline-none"
                />
              </div>

              {createError && <p className="text-sm text-red-600">{createError}</p>}

              <Button
                variant="raised"
                onClick={() => void handleCreateSession()}
                disabled={creating || !canCreate}
                className="w-full py-3 text-base"
              >
                {creating ? 'Creating…' : 'Create Session'}
              </Button>
            </div>
          </Card>
        )}
      </section>

      {/* Sessions list */}
      <section className="reveal" style={{ animationDelay: '120ms' }}>
        <h2 className="text-lg font-semibold text-[#1a1a1a] mb-4">All Sessions</h2>
        {sessions.length === 0 ? (
          <p className="text-sm text-[#aaaaaa]">No sessions yet. Create one above.</p>
        ) : (
          <div className="space-y-6">
            {groupSessionsByDay(sessions).map(([dayLabel, daySessions]) => (
              <div key={dayLabel}>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-[#aaaaaa] mb-2">{dayLabel}</h3>
                <div className="space-y-3">
                  {daySessions.map((s) => (
                    <Card key={s.id} className="p-4 flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusColor[s.status]}`}>
                      {statusLabel[s.status]}
                    </span>
                    {s.mode === 'design-partner' && (
                      <span className="rounded-full bg-brand-50 text-brand-700 text-xs px-2 py-0.5">Flo</span>
                    )}
                    <p className="font-medium text-[#1a1a1a] text-sm truncate">{s.title}</p>
                  </div>
                  <p className="text-xs text-[#aaaaaa] font-mono tracking-wider">
                    Code: {s.code}
                    {s.mode === 'design-partner' ? ' · AI chat session' : ` · ${s.questions.length} questions`}
                  </p>
                </div>
                <div className="flex gap-2 shrink-0">
                  {s.status === 'ended' ? (
                    <Link to={`/dashboard/sessions/${s.id}/review`}>
                      <Button variant="raisedoutline" className="text-xs border-[#eeeeee]">
                        Review
                      </Button>
                    </Link>
                  ) : (
                    <>
                      <Link to={`/dashboard/sessions/${s.id}`}>
                        <Button variant="raisedoutline" className="text-xs border-[#eeeeee]">
                          Open
                        </Button>
                      </Link>
                      <Button
                        variant="raisedoutline"
                        className="text-xs border-red-200 text-red-600 hover:bg-red-50"
                        disabled={closingId === s.id}
                        onClick={() => void handleCloseSession(s.id)}
                      >
                        {closingId === s.id ? '…' : 'End'}
                      </Button>
                    </>
                  )}
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
